import React, { useCallback, useEffect, useState } from 'react'
import SnackBarUtils from '../../../utils/SnackBarUtils'
import { IJobs } from './Facturacion'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ServerPagination, { IPaginator } from '../../../components/Common/PaginatorServer'
import DialogError from './DialogError'

export interface PropsJobs {
  headers: {
    key: string
    label: string
    orden: boolean
  }[]
  loading: boolean
  jobs: IJobs[]
  paginator: IPaginator
  handleConsumidorFinal: (idJob: number) => void
  fetchData?: (pagina: number, size: number, orden?: { key: string; dir: string }) => Promise<void>
}

const MAX_ERROR_LENGTH = 50

export const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return
  return new Date(dateStr).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export const getEstadoColor = (estado: string): 'success' | 'error' | 'warning' | 'default' | 'info' => {
  const lower = estado?.toLowerCase() ?? ''
  if (lower.includes('completado') || lower.includes('exitoso') || lower.includes('ok')) return 'success'
  if (lower.includes('error') || lower.includes('fallido')) return 'error'
  if (lower.includes('pendiente') || lower.includes('proceso')) return 'warning'
  if (lower.includes('nuevo')) return 'info'
  return 'default'
}

const TablaJobs: React.FC<PropsJobs> = ({ headers, loading, jobs, paginator, handleConsumidorFinal, fetchData }) => {
  const [orden, setOrden] = useState<{ key: string; dir: string }>(null)
  const [error, setError] = useState<string>(null)
  const [openError, setOpenError] = useState<boolean>(false)

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    )
  }

  const handleOrder = (key: string) => {
    const nuevoOrden: { key: string; dir: 'asc' | 'desc' } =
      orden?.key === key && orden.dir === 'desc' ? { key, dir: 'asc' } : { key, dir: 'desc' }

    setOrden(nuevoOrden)
    fetchData && fetchData(paginator.pageNumber, paginator.pageSize, nuevoOrden)
  }

  return (
    <>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              {headers.map((h) => (
                <TableCell key={h.key} sx={{ fontWeight: 'bold', fontSize: '18px', color: 'black' }}>
                  {h.orden ? (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => handleOrder(h.key)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        color: orden?.key === h.key ? 'primary.main' : 'black',
                        padding: 0
                      }}
                    >
                      {h.label}
                      {orden?.key === h.key ? (
                        orden.dir === 'desc' ? (
                          <ArrowDownwardIcon fontSize="small" />
                        ) : (
                          <ArrowUpwardIcon fontSize="small" />
                        )
                      ) : (
                        <ArrowDownwardIcon fontSize="small" sx={{ opacity: 0.25 }} />
                      )}
                    </Box>
                  ) : (
                    h.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {jobs.map((job: IJobs) => {
              const isTruncated = job.error?.length > MAX_ERROR_LENGTH
              const displayText = isTruncated ? job.error?.slice(0, MAX_ERROR_LENGTH) + '…' : job.error
              return (
                <TableRow
                  key={job.idFacturacionJob}
                  sx={{ '& td': { whiteSpace: 'normal', wordBreak: 'break-word', fontSize: '14px' } }}
                >
                  <TableCell>{job.idFacturacionJob}</TableCell>
                  <TableCell>{job.idContratoCtaCte}</TableCell>
                  <TableCell>{job.transactionId}</TableCell>
                  <TableCell>{job.nombreContratoProducto}</TableCell>
                  <TableCell>
                    <Chip
                      label={job.estado}
                      color={getEstadoColor(job.estado)}
                      size="small"
                      sx={{ fontWeight: 500, fontSize: '12px' }}
                    />
                  </TableCell>
                  <TableCell align="center">{job.reintentos ?? 0}</TableCell>
                  <TableCell>{job.numeroDocumento}</TableCell>
                  <TableCell>{job.cae ? 'Sí' : 'No'}</TableCell>
                  <TableCell>
                    {job.nombreLocal} ({job.codigoLocal})
                  </TableCell>
                  <TableCell>{formatDate(job.fechaCreacion)}</TableCell>
                  <TableCell>{formatDate(job.fechaActualizacion)}</TableCell>
                  <TableCell>{formatDate(job.fechaInicioProceso)}</TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    {job.error && (
                      <>
                        <Typography
                          variant="body2"
                          color="error"
                          fontSize={'14px'}
                          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                        >
                          {displayText}
                        </Typography>
                        {isTruncated && (
                          <Button
                            sx={{ mt: '0.5rem' }}
                            onClick={() => {
                              setError(job.error)
                              setOpenError(true)
                            }}
                            size="small"
                            color="inherit"
                            variant="outlined"
                          >
                            Ver más
                          </Button>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {(job.estado === 'SIN_CUIT' || job.forzarConsumidorFinal) && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleConsumidorFinal(job.idFacturacionJob)}
                      >
                        Cons final
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <ServerPagination component={'td'} label="Jobs por página" paginator={paginator} />
            </TableRow>
          </TableFooter>
        </Table>
      </Box>
      <DialogError open={openError} setOpen={setOpenError} error={error} setError={setError} />
    </>
  )
}

export default TablaJobs
